<script setup>
import { computed, ref, watch, inject, onMounted, onUnmounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useActionCenterStore } from '../../stores/ActionCenterStore'
import { resolveYearForDate, formatMMMDYYYY, toComparable, employerGroupKey, isPast, aggregateRequirementsFromFlatEvents, extractActiveEventsFromPlanYears, normalizeEventDateMD, requirementGroupKey } from './helpers'
import EmployerGroupedTable from './EmployerGroupedTable.vue'
import RequirementGroupedTable from './RequirementGroupedTable.vue'
import CalendarRange from '../common/CalendarRange.vue'
import SelectionAlert from './common/SelectionAlert.vue'
import SentRemindersModal from '../Reminders/SentRemindersModal.vue'
import BcsDynamicText from '../BcsDynamicText.vue'
import { ActionCenterRemindersService } from '../../services/ActionCenterRemindersService'
import emptyClipboardIcon from '../../../assets/images/empty-clipboard-icon.svg'
import AlertNotification from '../AlertNotification.vue'
import { BOOK_VIEWS } from './constants'

const props = defineProps({
  viewMode: {
    type: String,
    default: 'requirements-by-type'
  },
  searchTerm: {
    type: String,
    default: ''
  }
})

const actionCenterStore = useActionCenterStore()
const { filteredPlanYears, lastRemindersSummary, wasNext30DaysFilterActive, historicalReminders, selectedFilters, savedDeadlinesSelection, remindersBannerVisible } = storeToRefs(actionCenterStore)
const { setRemindersBannerVisible } = actionCenterStore

const functionsBaseUrl = inject('functionsBaseUrl')
const remindersService = ActionCenterRemindersService(functionsBaseUrl)
const emitter = inject('emitter')

const groupBy = ref(savedDeadlinesSelection.value?.groupBy || 'requirement')
const dateRange = ref({ start: null, end: null })
const sortAsc = ref(true)
const selectedGroupKeys = ref(
  savedDeadlinesSelection.value?.selectedKeys?.length
    ? new Set(savedDeadlinesSelection.value.selectedKeys)
    : new Set()
)
const next30DaysFilterActive = ref(savedDeadlinesSelection.value?.next30DaysFilterActive || false)
const calendarRangeRef = ref(null)
const lastClickedIndex = ref(-1)
const hasInitializedView = ref(false)

watch(groupBy, () => {
  selectedGroupKeys.value = new Set()
  lastClickedIndex.value = -1
})

watch(() => props.searchTerm, (newTerm, oldTerm) => {
  if (newTerm !== oldTerm) {
    clearSelections()
  }
})

watch(
  () => ({
    accountOwner: selectedFilters.value.accountOwner,
    planYearStartDate: selectedFilters.value.planYearStartDate,
    erisaPlan: selectedFilters.value.erisaPlan,
    medicalPlan: selectedFilters.value.medicalPlan,
    coverages: selectedFilters.value.coverages,
    fullTimeEmployees: selectedFilters.value.fullTimeEmployees
  }),
  () => {
    clearSelections()
  },
  { deep: true }
)

const next30DaysBannerMessage = computed(() => {
  if (!lastRemindersSummary.value || !wasNext30DaysFilterActive.value) {
    return ''
  }

  const completed = lastRemindersSummary.value.completed || 0
  const uniqueEmployers = new Set(
    (lastRemindersSummary.value.details || []).map(detail => detail.employerId)
  )
  const employerCount = uniqueEmployers.size

  return `${completed} reminder${completed !== 1 ? 's' : ''} for requirements due within the next 30 days have been sent to ${employerCount} employer${employerCount !== 1 ? 's' : ''}. Keep going to get ahead of the game!`
})

const aggregatedReminders = computed(() => {
  if (!lastRemindersSummary.value?.details) {
    return []
  }

  return remindersService.aggregateReminderResults(lastRemindersSummary.value.details)
})

function toggleSortModal() {
  sortDirectionModal.value = sortDirectionModal.value === 'asc' ? 'desc' : 'asc'
}

function toggleSortHistoricalModal() {
  sortDirectionHistoricalModal.value = sortDirectionHistoricalModal.value === 'asc' ? 'desc' : 'asc'
}

const aggregatedHistoricalReminders = computed(() => {
  if (!historicalReminders.value || historicalReminders.value.length === 0) {
    return []
  }

  const planYearEmployerMap = new Map()
  for (const planYear of filteredPlanYears.value) {
    if (planYear.id && planYear.employer?.name) {
      planYearEmployerMap.set(planYear.id, planYear.employer.name)
    }
  }

  const transformedReminders = historicalReminders.value.map(reminder => {
    const employerName = planYearEmployerMap.get(reminder.plan_year_id) || reminder.employer_name || ''

    return {
      requirementKey: reminder.requirement_key || null,
      jobId: reminder.job_id || null,
      name: reminder.name || '',
      due_date: reminder.due_date || '',
      start_date: reminder.start_date,
      created_at: reminder.created_at || '',
      employerId: reminder.employer_id || '',
      employerName,
      reminderId: reminder.id || null,
      calendarLink: reminder.calendar_link || '',
      attendeesCount: reminder.target_emails ? reminder.target_emails.split(',').filter(email => email.trim()).length : 0,
      target_emails: reminder.target_emails || '',
      success: reminder.status === 'sent',
      error: null
    }
  })

  return remindersService.aggregateReminderResults(transformedReminders)
})

const employersWithEvents = computed(() => {
  const employers = filteredPlanYears.value.map(planYear => {
    const employerName = planYear.employer?.name || 'Unknown Employer'
    const rawEvents = Array.isArray(planYear.complianceEvents?.results) ? planYear.complianceEvents.results : []
    const eventsWithResolvedDates = rawEvents.map(event => {
      const due = resolveYearForDate(event.date, planYear.plan_start_date)
      return { ...event, dueDateObj: due, dueDateStr: formatMMMDYYYY(due) }
    })

    const groupsMap = new Map()
    for (const event of eventsWithResolvedDates) {
      if (!groupsMap.has(event.dueDateStr)) {
        groupsMap.set(event.dueDateStr, [])
      }
      groupsMap.get(event.dueDateStr).push(event)
    }

    const groups = Array.from(groupsMap.entries())
      .map(([dueDateStr, events]) => ({
        dueDateStr,
        dueDateObj: events[0].dueDateObj,
        events
      }))
      .filter(group => !isPast(group.dueDateObj))
      .sort((a, b) => toComparable(a.dueDateObj) - toComparable(b.dueDateObj))

    const employerId = planYear.employer?.id || planYear.employer_id
    const employerForKey = {
      employerId,
      employerName,
      plan_start_date: planYear.plan_start_date
    }

    return {
      employerId,
      employerName,
      groups: groups.map(group => ({
        ...group,
        key: employerGroupKey(employerForKey, group)
      })),
      ...planYear
    }
  })

  const parsedTerm = (props.searchTerm || '').trim().toLowerCase()
  const filtered = parsedTerm
    ? employers.filter(employer =>
        employer.employerName.toLowerCase().includes(parsedTerm) ||
        employer.groups.some(group => group.events.some(event => event.name.toLowerCase().includes(parsedTerm)))
      )
    : employers

  const start = dateRange.value.start ? new Date(dateRange.value.start) : null
  const end = dateRange.value.end ? new Date(dateRange.value.end) : null

  const filteredByDate = (start || end)
    ? filtered.map(employer => ({
        ...employer,
        groups: employer.groups.filter(group => {
          const date = group.dueDateObj
          if (start && date < new Date(start.getFullYear(), start.getMonth(), start.getDate())) {
            return false
          }
          if (end && date > new Date(end.getFullYear(), end.getMonth(), end.getDate())) {
            return false
          }
          return true
        })
      })).filter(employer => employer.groups.length > 0)
    : filtered

  filteredByDate.sort((a, b) => a.employerName.localeCompare(b.employerName))
  if (!sortAsc.value) {
    filteredByDate.reverse()
  }

  return filteredByDate.filter(employer => employer.groups.length > 0)
})


const requirementsGroups = computed(() => {
  const flatEvents = extractActiveEventsFromPlanYears(filteredPlanYears.value, {
    dateStart: dateRange.value.start,
    dateEnd: dateRange.value.end,
    searchTerm: props.searchTerm
  })

  let groups = aggregateRequirementsFromFlatEvents(flatEvents)
  groups = groups
    .filter(group => group.employers.length > 0)
    .sort((a, b) => a.requirementName.localeCompare(b.requirementName))

  if (!sortAsc.value) {
    groups.reverse()
  }

  return groups
})


const hasTableResults = computed(() => {
  return employersWithEvents.value.length > 0 || requirementsGroups.value.length > 0
})

const isEmptyDueToDateRange = computed(() => {
  return !hasTableResults.value && dateRange.value.start && dateRange.value.end
})

const emptyStateTitle = computed(() => {
  return isEmptyDueToDateRange.value ? 'titleEmptyDueDateRange' : 'titleEmptyDuePlanYear'
})

const emptyStateSubtitle = computed(() => {
  return isEmptyDueToDateRange.value ? 'subtitleEmptyDueDateRange' : 'subtitleEmptyDuePlanYear'
})


function exportToCSV() {
  let headers = []
  let rows = []

  if (groupBy.value === 'requirement') {
    headers = ['Requirement', 'Due Date', 'Employers']
    rows = requirementsGroups.value.map(requirement => {
      const employersList = requirement.employers.map(employer => employer.employerName).join(', ')
      return [
        `"${requirement.requirementName.replace(/"/g, '""')}"`,
        `"${requirement.earliestDueDateObj ? formatMMMDYYYY(requirement.earliestDueDateObj) : ''}"`,
        `"${employersList.replace(/"/g, '""')}"`
      ]
    })
  } else {
    headers = ['Employer', 'Due Date', 'Requirements']
    employersWithEvents.value.forEach(employer => {
      employer.groups.forEach(group => {
        const requirementsList = group.events.map(event => event.name).join(', ')
        rows.push([
          `"${employer.employerName.replace(/"/g, '""')}"`,
          `"${group.dueDateStr}"`,
          `"${requirementsList.replace(/"/g, '""')}"`
        ])
      })
    })
  }

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', groupBy.value === 'requirement' ? 'requirements_deadlines.csv' : 'employer_deadlines.csv')
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function toggleGroupSelection(key) {
  const nextSelectedKeys = new Set(selectedGroupKeys.value)
  if (nextSelectedKeys.has(key)) {
    nextSelectedKeys.delete(key)
  } else {
    nextSelectedKeys.add(key)
  }
  selectedGroupKeys.value = nextSelectedKeys
}

const flatVisibleKeys = computed(() => {
  if (groupBy.value === 'employer') {
    const keys = []
    for (const employer of employersWithEvents.value) {
      for (const group of employer.groups) {
        keys.push(group.key)
      }
    }
    return keys
  }

  return requirementsGroups.value.map(requirement => requirement.key)
})

const sentRemindersLookup = computed(() => {
  const lookup = new Set()
  const reminders = historicalReminders.value || []

  for (const reminder of reminders) {
    if (reminder.employer_id && reminder.requirement_key) {
      lookup.add(`${reminder.employer_id}::${reminder.requirement_key}`)
    }
  }

  return lookup
})

function hasReminderBeenSent(employerId, requirementKey) {
  return sentRemindersLookup.value.has(`${employerId}::${requirementKey}`)
}

const isSelectAllInViewChecked = computed(() => {
  const keys = flatVisibleKeys.value
  return keys.length > 0 && keys.every(key => selectedGroupKeys.value.has(key))
})

function handleToggleSelectAll(checked) {
  const nextSelectedKeys = new Set(selectedGroupKeys.value)

  if (groupBy.value === 'employer') {
    const keys = []
    for (const employer of employersWithEvents.value) {
      for (const group of employer.groups) {
        keys.push(group.key)
      }
    }

    if (checked) {
      for (const key of keys) {
        nextSelectedKeys.add(key)
      }
    } else {
      for (const key of keys) {
        nextSelectedKeys.delete(key)
      }
    }
  } else {
    const keys = requirementsGroups.value.map(requirement => requirement.key)
    if (checked) {
      for (const key of keys) {
        nextSelectedKeys.add(key)
      }
    } else {
      for (const key of keys) {
        nextSelectedKeys.delete(key)
      }
    }
  }

  selectedGroupKeys.value = nextSelectedKeys
}

function handleToggleRow(payload) {
  const { key, index, shiftKey } = payload

  if (shiftKey && lastClickedIndex.value !== -1 && index !== -1) {
    const keys = flatVisibleKeys.value
    const start = Math.min(lastClickedIndex.value, index)
    const end = Math.max(lastClickedIndex.value, index)
    const nextSelectedKeys = new Set(selectedGroupKeys.value)
    const shouldSelect = !nextSelectedKeys.has(key)

    for (let i = start; i <= end; i += 1) {
      if (shouldSelect) {
        nextSelectedKeys.add(keys[i])
      } else {
        nextSelectedKeys.delete(keys[i])
      }
    }

    selectedGroupKeys.value = nextSelectedKeys
  } else {
    toggleGroupSelection(key)
  }

  lastClickedIndex.value = index
}

const selectedGroupsCount = computed(() => {
  let count = 0

  if (groupBy.value === 'employer') {
    for (const employer of employersWithEvents.value) {
      for (const group of employer.groups) {
        if (selectedGroupKeys.value.has(group.key)) {
          count += 1
        }
      }
    }
  } else {
    for (const requirement of requirementsGroups.value) {
      if (selectedGroupKeys.value.has(requirement.key)) {
        count += 1
      }
    }
  }

  return count
})

function clearSelections() {
  selectedGroupKeys.value = new Set()
  lastClickedIndex.value = -1
}

function buildFlatSelectedEvents() {
  const payload = []

  if (groupBy.value === 'employer') {
    for (const employer of employersWithEvents.value) {
      for (const group of employer.groups) {
        if (selectedGroupKeys.value.has(group.key)) {
          for (const event of group.events) {
            payload.push({
              employerId: employer.employerId,
              employerName: employer.employerName,
              contactEmails: employer.employer?.contact_emails || '',
              plan_start_date: employer.plan_start_date,
              plan_year_id: employer.id || employer.plan_year_id || null,
              dueDateObj: group.dueDateObj,
              dueDateStr: group.dueDateStr,
              eventId: event.id,
              eventName: event.name,
              eventBody: event.body,
              eventOriginalDateMD: normalizeEventDateMD(group.dueDateObj)
            })
          }
        }
      }
    }
  } else {
    for (const requirement of requirementsGroups.value) {
      if (selectedGroupKeys.value.has(requirement.key)) {
        for (const employer of requirement.employers) {
          payload.push({
            employerId: employer.employerId,
            employerName: employer.employerName,
            contactEmails: employer.contactEmails || '',
            plan_start_date: employer.plan_start_date,
            plan_year_id: employer.plan_year_id,
            dueDateObj: employer.dueDateObj,
            dueDateStr: employer.dueDateStr,
            eventId: employer.eventId,
            eventName: employer.eventName,
            eventBody: requirement.eventBody,
            eventOriginalDateMD: employer.eventOriginalDateMD
          })
        }
      }
    }
  }

  return payload
}

function onSendReminders() {
  const flatEvents = buildFlatSelectedEvents()
  const aggregatedRequirements = aggregateRequirementsFromFlatEvents(flatEvents).map(group => ({
    ...group,
    earliestDueDateStr: group.earliestDueDateObj ? formatMMMDYYYY(group.earliestDueDateObj) : '',
    selected: true,
    reminderBusinessDays: null,
    reminderDate: null
  }))

  if (emitter) {
    emitter.emit('action-center:start-send-reminders', {
      requirements: aggregatedRequirements,
      wasNext30DaysFilterActive: next30DaysFilterActive.value
    })
  }
}

function toggleSort() {
  sortAsc.value = !sortAsc.value
}

function onEventClicked(employerRow, groupEvent) {
  if (!emitter) {
    return
  }

  emitter.emit('action-center:event-clicked', {
    event: groupEvent,
    employer: {
      name: employerRow.employerName,
      plan_start_date: employerRow.plan_start_date,
      id: employerRow.employerId
    },
    source: 'action-center'
  })
}

function onRangeDate(range) {
  clearSelections()
  dateRange.value = {
    start: range?.start || null,
    end: range?.end || null
  }

  if (next30DaysFilterActive.value) {
    next30DaysFilterActive.value = false
  }
}

function onCalendarClear() {
  next30DaysFilterActive.value = false
  clearSelections()
}

function restoreNext30DaysRange() {
  const start = new Date()
  const end = new Date()
  end.setDate(end.getDate() + 30)

  if (calendarRangeRef.value) {
    calendarRangeRef.value.applyQuickRange(30, { silent: true })
  }

  dateRange.value = { start, end }
  next30DaysFilterActive.value = true
}

function applyNext30DaysFilter() {
  groupBy.value = 'employer'

  if (calendarRangeRef.value) {
    calendarRangeRef.value.applyQuickRange(30)
  } else {
    restoreNext30DaysRange()
  }

  next30DaysFilterActive.value = true
  // TODO: Re-enable this logic when ready to restore selection state
  // nextTick(() => {
  //   const selectableKeys = new Set()

  //   for (const employer of employersWithEvents.value) {
  //     for (const group of employer.groups) {
  //       const hasUnremindedEvent = group.events.some(event => {
  //         const reqKey = requirementGroupKey(event.body, normalizeEventDateMD(group.dueDateObj))
  //         return !hasReminderBeenSent(employer.employerId, reqKey)
  //       })
  //       if (hasUnremindedEvent) {
  //         selectableKeys.add(group.key)
  //       }
  //     }
  //   }

  //   selectedGroupKeys.value = selectableKeys
  // })
}

function clearNext30DaysFilterState({ clearSelections: shouldClearSelections = true } = {}) {
  if (calendarRangeRef.value) {
    calendarRangeRef.value.clearDateRange()
  } else {
    dateRange.value = { start: null, end: null }
  }

  next30DaysFilterActive.value = false

  if (shouldClearSelections) {
    clearSelections()
  }
}

function openRequirementsByEmployerView() {
  actionCenterStore.setBookDetailsView(BOOK_VIEWS.REQUIREMENTS_BY_EMPLOYER)
}

function applyViewMode(viewMode, { initial = false } = {}) {
  if (viewMode === BOOK_VIEWS.UPCOMING_REQUIREMENTS) {
    groupBy.value = 'employer'

    if (initial && next30DaysFilterActive.value) {
      restoreNext30DaysRange()
      return
    }

    applyNext30DaysFilter()
    return
  }

  if (next30DaysFilterActive.value || dateRange.value.start || dateRange.value.end) {
    clearNext30DaysFilterState({ clearSelections: !initial })
  }

  groupBy.value = viewMode === BOOK_VIEWS.REQUIREMENTS_BY_EMPLOYER ? 'employer' : 'requirement'
}

watch(
  () => props.viewMode,
  (viewMode, previousViewMode) => {
    if (!hasInitializedView.value || viewMode === previousViewMode) {
      return
    }

    applyViewMode(viewMode)
  }
)

onMounted(() => {
  if (emitter) {
    emitter.on('action-center:set-deadlines-group-by', newGroupBy => {
      if (newGroupBy === 'employer' || newGroupBy === 'requirement') {
        groupBy.value = newGroupBy
      }
    })

    emitter.on('action-center:apply-next-30-days-filter', () => {
      if (props.viewMode === 'upcoming-requirements') {
        applyNext30DaysFilter()
        return
      }

      actionCenterStore.setBookDetailsView(BOOK_VIEWS.UPCOMING_REQUIREMENTS)
    })
  }

  applyViewMode(props.viewMode, { initial: true })
  hasInitializedView.value = true
})

onUnmounted(() => {
  if (selectedGroupKeys.value.size > 0) {
    savedDeadlinesSelection.value = {
      groupBy: groupBy.value,
      selectedKeys: Array.from(selectedGroupKeys.value),
      next30DaysFilterActive: next30DaysFilterActive.value
    }
  } else {
    savedDeadlinesSelection.value = null
  }

  if (emitter) {
    emitter.off('action-center:set-deadlines-group-by')
    emitter.off('action-center:apply-next-30-days-filter')
  }
})

defineExpose({ clearSelections, employersWithEvents, requirementsGroups, groupBy })
</script>

<template>
  <div id="bookRequirementDeadlinesSection" class="d-flex justify-content-center mt-4 pt-1.5">
    <div class="w-100">
      <div v-if="remindersBannerVisible" class="mb-3">
        <AlertNotification
          :showAlert="remindersBannerVisible"
          @update:showAlert="setRemindersBannerVisible"
          type-alert="success"
        >
          <template v-if="wasNext30DaysFilterActive">
            <span class="ms-1 text-16"><strong>{{ next30DaysBannerMessage }}</strong></span>
          </template>
          <template v-else>
            <strong>
              <span style="padding-right: 8px">
                Reminders have been sent!
              </span>
              <a
                href=""
                data-bs-toggle="modal"
                data-bs-target="#summary-reminders-sent"
                @click.prevent
              >
                View Reminders Sent ({{ lastRemindersSummary?.completed ?? 0 }})
              </a>
            </strong>
          </template>
        </AlertNotification>
      </div>

      <SelectionAlert v-if="!next30DaysFilterActive" :count="selectedGroupsCount" @clear="clearSelections" />

      <div class="card border-0">
        <div v-if="next30DaysFilterActive" class="card-header bg-white d-flex align-items-center py-3 px-4 border-bottom">
          <span class="text-16 fw-medium me-3">Active Filter:</span>
          <button
            id="clearNext30DaysFilterBtn"
            class="btn btn-info btn-sm d-flex align-items-center px-2 text-14"
            @click="openRequirementsByEmployerView"
          >
            <med class="d-flex align-items-center">
              <i class="bi bi-x fs-5"></i>
              <span>Prioritized Reminders: Deadlines Within 30 Days</span>
            </med>
          </button>
        </div>

        <div id="requirementDeadlinesTableMenu" class="card-header bg-light d-flex justify-content-between align-items-center py-3 px-4">
          <div id="selectDateRangeRequirement" class="btn-group">
            <CalendarRange
              ref="calendarRangeRef"
              :model-value="dateRange"
              :disabled="!hasTableResults && !dateRange.start && !dateRange.end"
              @rangeDate="onRangeDate"
              @clear="onCalendarClear"
            />
          </div>
          <button id="requirementDeadlinesExportBtn" class="btn btn-link text-primary text-decoration-none text-16 p-0" :disabled="!hasTableResults" @click="exportToCSV">
            <med>{{ groupBy === 'requirement' ? 'Export Deadlines Grouped by Requirement CSV' : 'Export Action & Risk Data CSV' }}</med>
            <i class="bi bi-download ms-1"></i>
          </button>
        </div>

        <div class="card-body py-0 px-0">
          <div v-if="!hasTableResults" class="empty-table-state text-center py-5">
            <img :src="emptyClipboardIcon" alt="No requirements" class="mb-3" />
            <p class="header-18 mb-1">
              <strong><BcsDynamicText :text="emptyStateTitle" /></strong>
            </p>
            <p class="text-16 text-gray-600 mb-3">
              <BcsDynamicText :text="emptyStateSubtitle" />
            </p>
            <a
              v-if="!isEmptyDueToDateRange && historicalReminders.length > 0"
              href=""
              class="btn btn-outline-primary"
              data-bs-toggle="modal"
              data-bs-target="#historical-reminders-sent"
              @click.prevent
            >
              <i class="bi bi-bell me-2"></i>See Reminders Sent
            </a>
          </div>
          <div v-else class="table-responsive">
            <component
              :is="groupBy === 'employer' ? EmployerGroupedTable : RequirementGroupedTable"
              :employers="groupBy === 'employer' ? employersWithEvents : undefined"
              :requirements="groupBy === 'requirement' ? requirementsGroups : undefined"
              :sort-asc="sortAsc"
              :selected-keys="selectedGroupKeys"
              :is-select-all-checked="isSelectAllInViewChecked"
              :historical-reminders="historicalReminders"
              :flat-keys="flatVisibleKeys"
              @toggle-sort="toggleSort"
              @toggle-select-all="handleToggleSelectAll"
              @toggle-row="handleToggleRow"
              @event-clicked="onEventClicked($event.employer, $event.event)"
            />
          </div>
        </div>
      </div>

      <SelectionAlert v-if="!next30DaysFilterActive" :count="selectedGroupsCount" @clear="clearSelections" />

      <div v-if="hasTableResults" class="d-flex justify-content-end align-items-center">
        <div class="d-flex align-items-center gap-3">
          <button
            v-if="next30DaysFilterActive"
            id="clearSelectionsBtn"
            class="btn btn-link text-primary text-decoration-none p-0"
            @click="openRequirementsByEmployerView"
          >
            <i class="bi bi-x-circle-fill me-2"></i>
            <strong>Clear Selections</strong>
          </button>
          <button id="sendRemindersBtn" class="btn btn-primary btn-lg" :disabled="selectedGroupsCount === 0" @click="onSendReminders">
            Send Reminders <i class="bi bi-bell-fill"></i>
          </button>
        </div>
      </div>
    </div>

    <SentRemindersModal
      v-if="lastRemindersSummary && lastRemindersSummary.details"
      modal-id="summary-reminders-sent"
      subtitle-id="subtitleModalRemindersSentAC"
      :google-reminders="aggregatedReminders"
      :show-google-reminders="true"
      :sort-direction="sortDirectionModal"
      :today-date="new Date().toISOString().slice(0, 10)"
      :actual-employer="{ name: 'Selected Employers' }"
      :is-action-center-mode="true"
      @toggle-sort="toggleSortModal"
    />

    <SentRemindersModal
      v-if="historicalReminders.length > 0"
      modal-id="historical-reminders-sent"
      subtitle-id="subtitleModalRemindersSentAC"
      :google-reminders="aggregatedHistoricalReminders"
      :show-google-reminders="true"
      :sort-direction="sortDirectionHistoricalModal"
      :today-date="new Date().toISOString().slice(0, 10)"
      :actual-employer="{ name: 'All Employers' }"
      :is-action-center-mode="true"
      @toggle-sort="toggleSortHistoricalModal"
    />
  </div>
</template>

<style scoped>
.table td, .table th, .card-header {
  border: 1px solid #C5CDD4;
}

.table tr:first-child th {
  border-top: none;
}

.empty-table-state {
  border: 1px solid #C5CDD4;
  border-top: none;
}

.empty-table-state img {
  width: 46px;
  height: 46px;
}

#clearNext30DaysFilterBtn {
  border-color: var(--bs-primary);
}
</style>
